"use client";

import { useQuery } from "@tanstack/react-query";

import { useSearchParams } from "next/navigation";

import FetchError from "@/components/ui/fetch-error";
import Loading from "@/components/ui/loading";
import { Row } from "react-bootstrap";

import OverviewTable from "./overview-table";

import api from "@/lib/api";

type SurveyOverviewProps = {
  study: {
    id: number;
    title: string;
    full_name: string;
    has_overview: boolean;
  };
  countries: string;
  year_started: number;
};

export default function OverviewHome() {
  const searchParams = useSearchParams();
  const study_name = searchParams.get("study") || undefined;
  const cohort_name = searchParams.get("cohort") || undefined;

  const {
    data: overviews = [],
    error,
    isPending,
  } = useQuery({
    queryKey: ["srv-overview-hcap"],
    queryFn: () => api.get<SurveyOverviewProps[]>(`/srv/home/?site_name=hcap`),
  });

  if (isPending) return <Loading />;
  if (error) return <FetchError message={error.message} />;

  return (
    <Row>
      <p>
        The HCAP studies were designed to enable cross-country comparative research on cognitive aging and dementia.
        Building on the rich history and existing infrastructure of the HRS family of studies, the first HCAP studies
        were designed as subsamples of existing HRS studies. As the HCAP has grown in popularity, its use is rapidly
        expanding across the globe, with new studies using the HCAP to measure cognition in subsamples of other
        longitudinal studies or using the HCAP for stand-alone studies in new countries.
      </p>
      <p className="mb-5">
        To enable high quality and comparable measurement of cognitive functioning across diverse contexts, the HCAP
        uses a structured but adaptable framework, while ensuring sufficient consistency to allow for statistical
        harmonization of cognitive functioning across studies. All HCAP studies also include assessments of cognition
        and functioning from the perspective of an informant (i.e., a close friend or family member). All HCAP studies
        distribute their data to the research community.
      </p>
      <h4 className="g2-header-navy">Overview</h4>
      <hr />
      <p>Find information about each study catalogued by the Gateway</p>
      <OverviewTable overviews={overviews} study_name={study_name} cohort_name={cohort_name} />
    </Row>
  );
}
